import { useContext } from 'react';
import {
  FlexContainer,
  Text,
  Layout,
  Buttons,
  ModalContext,
  Banner,
  Sizes,
} from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import useTranslation from 'Hooks/useTranslation';
import AutoRenewalModal from 'Containers/Result/components/AutoRenewalModal';
import { useAutoRenewal } from 'Containers/Filing/hooks';
import { FRONT_PLANS, plansNameMapping } from 'Constants/plans';
import { CustomerType } from 'Types/common';
import { getDateFormatted } from './utils';
import * as Styled from './styles';

const Dates = ({ subscription }: { subscription: CustomerType }): JSX.Element => {
  const { showModal } = useContext(ModalContext);
  const { mutate: autoRenewal } = useAutoRenewal();
  const { t } = useTranslation(locizeNS.ACCOUNT);
  const activePlan = plansNameMapping(subscription?.activePlan?.name);

  const handleTinyClick = (): void => {
    showModal({
      comp: <AutoRenewalModal />,
    });
  };

  const handleAutoRenewal = (): void => {
    autoRenewal({ isAutoRenewalEnabled: true });
  };

  return (
    <Layout.Wrap size="M" hasBorder={false}>
      <FlexContainer gap="dimension_32" wrap="wrap">
        <Styled.FlexContainer $subscription={subscription} gap="dimension_08" direction="column">
          {subscription?.created && (
            <>
              <Text type="paragraphs" size="S_L_S" color="dark">
                {t('Subscription.Main.Registration.Label')}
              </Text>
              <Text type="paragraphs" size="M_M_S" color="grey">
                {getDateFormatted({ date: subscription?.created })}
              </Text>
            </>
          )}
        </Styled.FlexContainer>
        {activePlan !== FRONT_PLANS.FLEX && activePlan !== FRONT_PLANS.BASIC && (
          <Styled.FlexContainer
            $subscription={subscription}
            gap="dimension_08"
            direction="column"
            align="flex-start"
          >
            <>
              <Text type="paragraphs" size="S_L_S" color="dark">
                {t('Subscription.Label.Finish')}
              </Text>
              <Text type="paragraphs" size="M_M_S" color="grey">
                {getDateFormatted({
                  date: subscription?.activePlan?.started,
                  lastDayOfYear: true,
                })}
              </Text>
            </>
          </Styled.FlexContainer>
        )}
        {activePlan === FRONT_PLANS.FLEX && (
          <Styled.FlexContainer
            $subscription={subscription}
            gap="dimension_08"
            direction="column"
            align="flex-start"
          >
            <>
              <Text type="paragraphs" size="S_L_S" color="dark">
                {t('Subscription.Label.Finish')}
              </Text>

              {subscription?.isAutoRenewalEnabled ? (
                <>
                  <Text type="paragraphs" size="M_M_S" color="grey">
                    {t('Subscription.Label.description', { context: activePlan })}
                  </Text>
                  <Buttons.Tiny label={t('Subscription.Label.Button')} onClick={handleTinyClick} />
                </>
              ) : (
                <>
                  <Text type="paragraphs" size="M_M_S" color="grey">
                    {getDateFormatted({
                      date: subscription?.activePlan?.started,
                      lastDayOfYear: true,
                    })}
                  </Text>
                  <Banner
                    id="auto-renewal-banner"
                    label={t('Subscription.Banner.AutoRenewal')}
                    mode="warning"
                    position="horizontally"
                    titleSize={Sizes.XS}
                    hasIcon
                    buttonLabel={t('Subscription.Banner.Button')}
                    onClick={handleAutoRenewal}
                    layout="embed"
                  />
                </>
              )}
            </>
          </Styled.FlexContainer>
        )}
      </FlexContainer>
    </Layout.Wrap>
  );
};

export default Dates;
